import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import TranscriptCard from './TranscriptCard';

import type { FirefliesTranscript } from '../types';

import LoadingSpinner from '@/common/components/LoadingSpinner';

interface TranscriptListProps {
  transcripts: FirefliesTranscript[];
  lastFetchedAt: number | null;
  loading: boolean;
  error: string | null;
  userEmail: string;
  hasNotesWidget: boolean;
  hasChecklistWidget: boolean;
  onRefresh: () => void;
  onClearError: () => void;
  onOpenDetail: (transcript: FirefliesTranscript) => void;
  onSaveToNotes: (transcript: FirefliesTranscript) => void;
  onAddToChecklist: (transcript: FirefliesTranscript) => void;
}

export default function TranscriptList({
  transcripts,
  lastFetchedAt,
  loading,
  error,
  userEmail,
  hasNotesWidget,
  hasChecklistWidget,
  onRefresh,
  onClearError,
  onOpenDetail,
  onSaveToNotes,
  onAddToChecklist,
}: TranscriptListProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex items-center justify-end gap-2 mb-2 shrink-0">
        {lastFetchedAt && (
          <span className="text-[11px] text-gray-400 mr-auto">
            {t('fireflies.lastUpdated', {
              minutes: Math.max(1, Math.floor((Date.now() - lastFetchedAt) / 60000)),
            })}
          </span>
        )}
        <button
          className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 px-2 py-1 rounded-md transition-colors disabled:opacity-50 disabled:pointer-events-none"
          disabled={loading}
          onClick={onRefresh}
        >
          <ArrowPathIcon className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {t('fireflies.refresh')}
        </button>
      </div>

      {error && (
        <div className="mb-2 p-2 bg-red-50 text-red-700 text-sm rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button className="text-red-500 hover:text-red-700" onClick={onClearError}>
            ×
          </button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {loading && transcripts.length === 0 ? (
          <LoadingSpinner />
        ) : transcripts.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-500 text-sm">No transcripts found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {transcripts.map((transcript) => (
              <TranscriptCard
                key={transcript.id}
                hasChecklistWidget={hasChecklistWidget}
                hasNotesWidget={hasNotesWidget}
                transcript={transcript}
                userEmail={userEmail}
                onAddToChecklist={onAddToChecklist}
                onOpenDetail={onOpenDetail}
                onSaveToNotes={onSaveToNotes}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
